import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import Breadcrumb from "../../components/navigation/Bredcrumbs";
import TopHeadding from "../../components/layout/TopHeadding";
import PayrollAccordion from "../../components/accordions/PayrollAccordian";
import StatusPopup from "../../components/popups/StatusPopup";
import DDPaidSuccessfully from "../../components/popups/DDPaidSuccessfully";
import FutureDateWarningPopup from "../../components/popups/FutureDateWarningPopup";
import FilterTopBar from "../../components/navigation/FilterTopBar";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import { Spinner } from "react-bootstrap";
import calendar_icon from "../../assets/images/calendar_icon.svg";
import tax_icon from "../../assets/images/taxview_icon.svg";

import {
  fetchPayrollDashboard,
  runPayrollCalulation,
  updateEmployeesWorkDetails,
  generatePayCheck,
  processPaymentDirectDeposit,
  selectUpdatingEmployeeIds,
} from "../../store/slices/payrollSlice";

const getCurrentMonthRange = () => {
  const now = new Date();
  return {
    start: new Date(now.getFullYear(), now.getMonth(), 1),
    end: new Date(now.getFullYear(), now.getMonth() + 1, 0),
  };
};

const formatDate = (date) => {
  if (!date) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getDashboardParams = (companyIds, startDate, endDate) => ({
  companyIds: companyIds,
  startDate: formatDate(startDate),
  endDate: formatDate(endDate),
});

const SelectedPayroll = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { id } = useParams();

  // Get user role from Redux store
  const { role } = useSelector((s) => s.user);

  const companyIds = useMemo(() => {
    if (location.state?.companies && location.state.companies.length > 0) {
      return location.state.companies
        .map((c) => {
          if (typeof c === "number") return c;
          if (typeof c === "string") return parseInt(c);
          return parseInt(c.id || c.value);
        })
        .filter(Boolean);
    } else if (id) {
      return [parseInt(id)];
    }
    return [];
  }, [location.state?.companies, id]);

  // Use first company ID for single-company operations
  const companyId = companyIds?.[0] || null;

  const DASHBOARD_KEY = "payrollDashboard";

  const dashboard = useSelector((s) => s.payroll.entityByKey[DASHBOARD_KEY]);

  const loading = useSelector(
    (s) => s.payroll.entityLoadingByKey[DASHBOARD_KEY],
  );

  const isCalculatingPayroll = useSelector((state) =>
    Object.keys(state.payroll.entityLoadingByKey).some(
      (key) =>
        key.startsWith("runPayroll-") && state.payroll.entityLoadingByKey[key],
    ),
  );

  const updatingEmployeeIds = useSelector(selectUpdatingEmployeeIds);

  const defaultRange = getCurrentMonthRange();
  const [startDate, setStartDate] = useState(defaultRange.start);
  const [endDate, setEndDate] = useState(defaultRange.end);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({ payRange: [0, 1000000] });

  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [successData, setSuccessData] = useState(null);

  const [showFuturePopup, setShowFuturePopup] = useState(false);
  const [futurePopupType, setFuturePopupType] = useState(null); // 'calculate' or 'pay'

  // Pending calculation state for Calculate DD
  const [pendingCalculation, setPendingCalculation] = useState(null);

  // Pending payment state for Pay DD
  const [pendingPayment, setPendingPayment] = useState(null);

  // Trigger to show DD confirmation popup from parent
  const [triggerDDConfirmation, setTriggerDDConfirmation] = useState(null);

  useEffect(() => {
    if (!companyId || !companyIds || companyIds.length === 0) return;

    dispatch(
      fetchPayrollDashboard({
        key: DASHBOARD_KEY,
        params: getDashboardParams(companyIds, startDate, endDate),
      }),
    );
  }, [companyId, companyIds, dispatch, startDate, endDate]);

  const transformedDashboard = useMemo(() => {
    if (!dashboard) return [];

    const hasCycles =
      dashboard[0]?.cycles && Array.isArray(dashboard[0].cycles);

    if (hasCycles) {
      return dashboard.map((company) => {
        const allPayrolls = [];

        company.cycles.forEach((cycle) => {
          cycle.payrollHeaders?.forEach((header) => {
            allPayrolls.push({
              ...header,
              cycleName: cycle.cycleName,
            });
          });
        });

        return { ...company, payrolls: allPayrolls };
      });
    }

    return dashboard;
  }, [dashboard]);

  const filteredCompanies = useMemo(() => {
    return transformedDashboard
      .map((company) => {
        const payrolls = company.payrolls
          .map((payroll) => {
            const filteredEmployees = payroll.employees.filter((emp) => {
              const netPay = emp.netPayAfterWH ?? 0;
              return (
                netPay >= filters.payRange[0] && netPay <= filters.payRange[1]
              );
            });

            return { ...payroll, employees: filteredEmployees };
          })
          .filter((payroll) => payroll.employees.length > 0);

        return { ...company, payrolls };
      })
      .filter((company) =>
        search
          ? company.companyName?.toLowerCase().includes(search.toLowerCase())
          : true,
      );
  }, [transformedDashboard, search, filters.payRange]);

  const openErrorPopup = (msg) => {
    setErrorMessage(msg);
    setShowErrorPopup(true);
  };

  const handleDateChange = ({ startDate, endDate }) => {
    setStartDate(startDate);
    setEndDate(endDate);
  };

  const performCalculatePay = async (
    companyId,
    cycleType,
    payrollFrom,
    payrollTo,
  ) => {
    try {
      await dispatch(
        runPayrollCalulation({
          key: `runPayroll-${companyId}`,
          params: { companyId, payrollFrom, payrollTo, cycleType },
        }),
      ).unwrap();

      dispatch(
        fetchPayrollDashboard({
          key: DASHBOARD_KEY,
          params: getDashboardParams(companyIds, startDate, endDate),
        }),
      );
    } catch {
      openErrorPopup("Failed to calculate payroll.");
    }
  };

  const handleFuturePopupConfirm = () => {
    // Handle Calculate DD if pending
    if (pendingCalculation) {
      performCalculatePay(
        pendingCalculation.companyId,
        pendingCalculation.cycleType,
        pendingCalculation.payrollFrom,
        pendingCalculation.payrollTo,
      );
      setPendingCalculation(null);
      setShowFuturePopup(false);
      setFuturePopupType(null);
      return; // Return early to prevent triggering PayDD popup
    }

    // Handle Pay DD if pending - show DDConfirmationPopup
    if (pendingPayment) {
      setShowFuturePopup(false);
      // Trigger the DD confirmation popup in PayrollAccordion
      setTriggerDDConfirmation({
        payrollHeaderId: pendingPayment.payrollHeaderId,
        company: pendingPayment.company,
      });
      // Clear after a short delay to reset trigger
      setTimeout(() => setTriggerDDConfirmation(null), 100);
    }
  };

  const handleUpdateWorkDetails = async (headerId, dto) => {
    const employeeId = dto?.[0]?.PayrollEmployeeId;

    try {
      await dispatch(
        updateEmployeesWorkDetails({
          key: `update-workDetail-${employeeId}`,
          headerId,
          dto,
        }),
      ).unwrap();

      dispatch(
        fetchPayrollDashboard({
          key: DASHBOARD_KEY,
          params: getDashboardParams(companyIds, startDate, endDate),
        }),
      );
    } catch {
      openErrorPopup("Failed to update work details.");
    }
  };

  const handleDirectDepositPayment = async (payrollHeaderId, company) => {
    try {
      const payroll = company.payrolls.find(
        (p) => p.payrollHeaderId === payrollHeaderId,
      );

      const employees = payroll?.employees ?? [];

      const snapshot = {
        companyName: company.companyName,
        employeeCount: employees.length,
        totalAmount: employees.reduce((s, e) => s + (e.netPayAfterWH || 0), 0),
      };

      await dispatch(
        processPaymentDirectDeposit({
          key: `paymentDD-${payrollHeaderId}`,
          payrollHeaderId,
        }),
      ).unwrap();

      setSuccessData(snapshot);
      setShowSuccessPopup(true);

      dispatch(
        fetchPayrollDashboard({
          key: DASHBOARD_KEY,
          params: getDashboardParams(companyIds, startDate, endDate),
        }),
      );
    } catch (error) {
      openErrorPopup(error?.message || "Payment failed.");
    }
  };

  const handleGeneratePayCheck = async (payrollEmployeeId) => {
    try {
      const result = await dispatch(
        generatePayCheck(payrollEmployeeId),
      ).unwrap();

      const blob = new Blob([result.file], {
        type: "application/pdf",
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Paycheck_${payrollEmployeeId}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      openErrorPopup("Failed to generate paycheck.");
    }
  };

  // Check if payroll dates are in the future
  const isFuturePayroll = (payrollFrom, payrollTo) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const fromDate = new Date(payrollFrom);
    const toDate = new Date(payrollTo);

    return fromDate > today || toDate > today;
  };

  // Handle Calculate DD click with future date check
  const handleCalculateClick = (
    companyId,
    cycleType,
    payrollFrom,
    payrollTo,
  ) => {
    // Check if payroll dates are in the future
    if (isFuturePayroll(payrollFrom, payrollTo)) {
      // Store pending calculation with full payload and show future warning popup
      setPendingCalculation({ companyId, cycleType, payrollFrom, payrollTo });
      setFuturePopupType("calculate");
      setShowFuturePopup(true);
    } else {
      // Proceed directly with calculation
      performCalculatePay(companyId, cycleType, payrollFrom, payrollTo);
    }
  };

  // Handle Pay DD click with future date check
  const handlePayDDClick = (
    payrollHeaderId,
    company,
    payrollFrom,
    payrollTo,
  ) => {
    // Check if payroll dates are in the future
    if (isFuturePayroll(payrollFrom, payrollTo)) {
      // Store pending payment and show future warning popup
      setPendingPayment({ payrollHeaderId, company });
      setFuturePopupType("pay");
      setShowFuturePopup(true);
    } else {
      // Show DDConfirmationPopup for non-future date
      setTriggerDDConfirmation({
        payrollHeaderId: payrollHeaderId,
        company: company,
      });
      // Clear after a short delay to reset trigger
      setTimeout(() => setTriggerDDConfirmation(null), 100);
    }
  };

  // Handle case when no company is selected
  if (!companyId && (!companyIds || companyIds.length === 0)) {
    return (
      <div className="selected_payroll">
        <Breadcrumb
          items={[
            { label: "Companies", link: "/companies" },
            { label: "Payroll", active: true },
          ]}
        />
        <div className="container-fluid mt_20">
          <div className="alert alert-warning">
            No company selected. Please select a company from the Client or
            Company detail page.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="selected_payroll">
      <Breadcrumb
        items={[
          { label: "Companies", link: "/companies" },
          { label: "Payroll", active: true },
        ]}
      />

      <TopHeadding
        title="Payroll"
        spanClass=""
        subtitle=""
        showDatepicker={false}
        buttons={
          role === "SuperAdmin"
            ? [
                {
                  text: "Tax History",
                  className: "btn_primary_dark",
                  icon: tax_icon,
                  onClick: () =>
                    navigate("/taxview", {
                      state: {
                        companyId,
                        startDate: formatDate(startDate),
                        endDate: formatDate(endDate),
                      },
                    }),
                },
              ]
            : []
        }
      />

      <div className="container-fluid mt_20">
        <div className="d-flex justify-content-between align-items-center top_headding">
          <div className="datepicker position-relative">
            <DatePicker
              selectsRange
              startDate={startDate}
              endDate={endDate}
              onChange={(update) => {
                const [start, end] = update;
                setStartDate(start);
                setEndDate(end);
              }}
              dateFormat="MM/dd/yyyy"
              className="form-control"
            />
            <img
              src={calendar_icon}
              className="datepicker_icon"
              alt="calendar"
            />
          </div>

          <FilterTopBar
            title="Employees"
            search={search}
            onSearchChange={setSearch}
            placeholder={"Search Company"}
            showExport={false}
            showFilter={true}
            onFilterChange={setFilters}
            startDate={startDate}
            endDate={endDate}
            onDateChange={handleDateChange}
            isPayrollPage={true}
          />
        </div>
      </div>

      {loading && (
        <div className="text-center mt_50">
          <Spinner animation="border" variant="primary" role="status">
            <span className="visually-hidden">Loading</span>
          </Spinner>
        </div>
      )}

      <PayrollAccordion
        companies={filteredCompanies}
        onCalculate={performCalculatePay}
        onCalculateClick={handleCalculateClick}
        onUpdateWorkDetails={handleUpdateWorkDetails}
        onDirectDepositPayment={handleDirectDepositPayment}
        updatingEmployeeIds={updatingEmployeeIds}
        onGeneratePayCheck={handleGeneratePayCheck}
        showErrorPopup={showErrorPopup}
        errorMessage={errorMessage}
        openErrorPopup={openErrorPopup}
        closeErrorPopup={() => setShowErrorPopup(false)}
        loading={loading}
        isCalculatingPayroll={isCalculatingPayroll}
        onPayDDClick={handlePayDDClick}
        triggerDDConfirmation={triggerDDConfirmation}
      />

      {!loading && !isCalculatingPayroll && filteredCompanies?.length === 0 && (
        <div className="no-data-message text-center mt_50">
          No payroll data available.
        </div>
      )}

      <StatusPopup
        show={showErrorPopup}
        type="error"
        message={errorMessage}
        handleClose={() => setShowErrorPopup(false)}
      />

      <DDPaidSuccessfully
        show={showSuccessPopup}
        onHide={() => setShowSuccessPopup(false)}
        companyName={successData?.companyName}
        employeeCount={successData?.employeeCount}
        totalAmount={successData?.totalAmount}
      />

      <FutureDateWarningPopup
        show={showFuturePopup}
        onHide={() => {
          setShowFuturePopup(false);
          setFuturePopupType(null);
        }}
        onConfirm={handleFuturePopupConfirm}
        message={
          futurePopupType === "calculate"
            ? "You are calculating the future date range payroll."
            : "You are processing payment for a future date range payroll."
        }
      />
    </div>
  );
};

export default SelectedPayroll;
